import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Spinner,
} from '@fc/ui'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Cake, Mail, MapPin, Phone, ShieldCheck, Users } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { PageHeader } from '../../components/page-header'
import { getPersonProfile, type PersonContact, type PersonProfile } from '../../lib/directory-api'

const statusVariant: Record<string, 'success' | 'neutral' | 'gold' | 'warning'> = {
  member: 'success',
  regular: 'gold',
  visitor: 'gold',
  inactive: 'neutral',
}

function initials(p: { firstName: string; lastName: string }): string {
  return `${p.firstName.at(0) ?? ''}${p.lastName.at(0) ?? ''}`.toUpperCase() || '·'
}

function formatDate(d: string | null): string {
  if (!d) return '—'
  const parsed = new Date(d)
  if (Number.isNaN(parsed.getTime())) return d
  return parsed.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

function contactIcon(kind: PersonContact['kind']) {
  switch (kind) {
    case 'email':
      return <Mail className="size-4 text-muted" aria-hidden />
    case 'phone':
      return <Phone className="size-4 text-muted" aria-hidden />
    case 'address':
      return <MapPin className="size-4 text-muted" aria-hidden />
    default:
      return <Users className="size-4 text-muted" aria-hidden />
  }
}

function contactDisplay(c: PersonContact): string {
  if (c.kind === 'address' && c.address) {
    const a = c.address as Record<string, string>
    return [a.line1, a.line2, a.city, a.region, a.postalCode].filter(Boolean).join(', ') || '—'
  }
  return c.value ?? '—'
}

function DataRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <dt className="text-xs uppercase tracking-wider text-dim">{label}</dt>
      <dd className="text-right text-sm text-fg">{value}</dd>
    </div>
  )
}

export function PersonProfilePage() {
  const { personId = '' } = useParams<{ personId: string }>()
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['person-profile', personId],
    queryFn: () => getPersonProfile(personId),
    enabled: Boolean(personId),
  })

  return (
    <div className="space-y-6">
      <PageHeader
        title="Profile"
        description="Full directory record."
        actions={
          <Button asChild variant="secondary">
            <Link to="/app/directory">
              <ArrowLeft />
              Back to directory
            </Link>
          </Button>
        }
      />

      {isLoading ? (
        <div className="flex items-center justify-center gap-2 py-20 text-muted">
          <Spinner /> Loading profile…
        </div>
      ) : isError ? (
        <Card className="py-16 text-center">
          <p className="text-sm text-danger">Couldn’t load this profile.</p>
          <p className="mt-1 text-xs text-dim">
            {error instanceof Error ? error.message : 'You may not have access.'}
          </p>
        </Card>
      ) : !data ? (
        <Card className="py-16 text-center text-sm text-muted">Profile not found.</Card>
      ) : (
        <Profile data={data} />
      )}
    </div>
  )
}

function Profile({ data }: { data: PersonProfile }) {
  const displayName = [data.prefix, data.firstName, data.lastName, data.suffix]
    .filter(Boolean)
    .join(' ')

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      {/* Identity column */}
      <div className="space-y-6 lg:col-span-1">
        <Card>
          <CardContent className="flex flex-col items-center gap-4 pt-2 text-center">
            <Avatar className="size-24">
              {data.photoUrl && <AvatarImage src={data.photoUrl} alt={displayName} />}
              <AvatarFallback className="text-xl">{initials(data)}</AvatarFallback>
            </Avatar>
            <div className="space-y-1">
              <h2 className="font-display text-xl font-bold">{displayName || 'Unnamed'}</h2>
              {data.nickname && <p className="text-sm text-muted">“{data.nickname}”</p>}
              <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
                <Badge variant={statusVariant[data.status] ?? 'neutral'} className="capitalize">
                  {data.status}
                </Badge>
                {data.isMinor && <Badge variant="warning">Minor</Badge>}
                {data.campus && <Badge variant="outline">{data.campus}</Badge>}
              </div>
            </div>
            {data.tags.length > 0 && (
              <div className="flex flex-wrap justify-center gap-1.5">
                {data.tags.map((tagName) => (
                  <Badge key={tagName} variant="primary">
                    {tagName}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Cake className="size-4 text-muted" aria-hidden /> Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="divide-y divide-border-soft">
              <DataRow label="Birthdate" value={formatDate(data.birthdate)} />
              <DataRow label="Gender" value={data.gender ?? '—'} />
              <DataRow label="Marital" value={data.maritalStatus ?? '—'} />
              <DataRow label="Anniversary" value={formatDate(data.anniversary)} />
              <DataRow label="Member since" value={formatDate(data.membershipDate)} />
              <DataRow label="Added" value={formatDate(data.createdAt)} />
            </dl>
          </CardContent>
        </Card>
      </div>

      {/* Main column */}
      <div className="space-y-6 lg:col-span-2">
        {/* Contacts */}
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Contact methods</CardTitle>
          </CardHeader>
          <CardContent>
            {!data.canViewContacts ? (
              <p className="flex items-center gap-2 py-4 text-sm text-dim">
                <ShieldCheck className="size-4" aria-hidden />
                You don’t have permission to view contact details.
              </p>
            ) : data.contacts.length === 0 ? (
              <p className="py-4 text-sm text-muted">No contact methods on file.</p>
            ) : (
              <ul className="divide-y divide-border-soft">
                {data.contacts.map((c) => (
                  <li key={c.id} className="flex items-center gap-3 py-2.5">
                    {contactIcon(c.kind)}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm text-fg">{contactDisplay(c)}</p>
                      {c.label && <p className="text-xs text-dim">{c.label}</p>}
                    </div>
                    <div className="flex items-center gap-1.5">
                      {c.isPrimary && <Badge variant="success">Primary</Badge>}
                      {c.optOut && <Badge variant="danger">Opted out</Badge>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Relationships */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Users className="size-4 text-muted" aria-hidden /> Relationships
            </CardTitle>
          </CardHeader>
          <CardContent>
            {data.relationships.length === 0 ? (
              <p className="py-4 text-sm text-muted">No relationships recorded.</p>
            ) : (
              <ul className="divide-y divide-border-soft">
                {data.relationships.map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-3 py-2.5">
                    <Link
                      to={`/app/directory/${r.relatedPersonId}`}
                      className="text-sm font-medium text-fg hover:text-secondary hover:underline"
                    >
                      {r.relatedPersonName}
                    </Link>
                    <div className="flex items-center gap-1.5">
                      <Badge variant="neutral" className="capitalize">
                        {r.relationshipType.replace('_', ' ')}
                      </Badge>
                      {r.isGuardian && <Badge variant="primary">Guardian</Badge>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Household */}
        {data.household && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">
                Household{data.household.name ? ` · ${data.household.name}` : ''}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="divide-y divide-border-soft">
                {data.household.members.map((m) => (
                  <li key={m.personId} className="flex items-center gap-3 py-2.5">
                    <Avatar className="size-8">
                      {m.photoUrl && <AvatarImage src={m.photoUrl} alt={m.name} />}
                      <AvatarFallback>
                        {m.name
                          .split(' ')
                          .map((s) => s.at(0) ?? '')
                          .slice(0, 2)
                          .join('')
                          .toUpperCase() || '·'}
                      </AvatarFallback>
                    </Avatar>
                    <Link
                      to={`/app/directory/${m.personId}`}
                      className="flex-1 text-sm font-medium text-fg hover:text-secondary hover:underline"
                    >
                      {m.name}
                    </Link>
                    {m.householdRole && (
                      <Badge variant="outline" className="capitalize">
                        {m.householdRole}
                      </Badge>
                    )}
                    {data.household?.primaryContactPersonId === m.personId && (
                      <Badge variant="success">Primary contact</Badge>
                    )}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Custom fields */}
        {data.customValues.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Custom fields</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="divide-y divide-border-soft">
                {data.customValues.map((v) => (
                  <DataRow
                    key={v.customFieldId}
                    label={v.label}
                    value={
                      Array.isArray(v.value)
                        ? v.value.join(', ') || '—'
                        : v.value === null || v.value === ''
                          ? '—'
                          : String(v.value)
                    }
                  />
                ))}
              </dl>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
